import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Badge, Button, Card, Input, Spinner, Textarea,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  Label, Alert,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@agion/ui'
import { Fingerprint, Plus, Search, Pencil, Trash2, Copy, Check } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import {
  listOIDCClients,
  createOIDCClient,
  updateOIDCClient,
  deleteOIDCClient,
} from './infra_oidc.service'
import type {
  OIDCClient,
  OIDCClientStatus,
  TokenEndpointAuthMethod,
  OIDCGrantType,
  CreateOIDCClientRequest,
  UpdateOIDCClientRequest,
} from '@/core/infra_types'
import { formatDate } from '@/core/infra_utils'

const statusVariant: Record<OIDCClientStatus, 'success' | 'warning' | 'error'> = {
  active: 'success',
  suspended: 'warning',
  revoked: 'error',
}

const AUTH_METHODS: { value: TokenEndpointAuthMethod; label: string }[] = [
  { value: 'none', label: 'None (Public / PKCE)' },
  { value: 'client_secret_post', label: 'Client Secret (POST)' },
  { value: 'client_secret_basic', label: 'Client Secret (Basic)' },
  { value: 'private_key_jwt', label: 'Private Key JWT' },
]

const GRANT_TYPES: { value: OIDCGrantType; label: string }[] = [
  { value: 'authorization_code', label: 'Authorization Code' },
  { value: 'client_credentials', label: 'Client Credentials' },
  { value: 'refresh_token', label: 'Refresh Token' },
  { value: 'urn:ietf:params:oauth:grant-type:device_code', label: 'Device Code' },
]

const COMMON_SCOPES = ['openid', 'profile', 'email', 'offline_access']

export function OIDCClientsPage() {
  const { t } = useTranslation(['pages'])
  const [clients, setClients] = useState<OIDCClient[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [editClient, setEditClient] = useState<OIDCClient | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<OIDCClient | null>(null)

  const loadClients = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await listOIDCClients()
      setClients(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load OIDC clients')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadClients()
  }, [loadClients])

  const handleDeleteConfirmed = async (clientId: string) => {
    try {
      await deleteOIDCClient(clientId)
      setClients(prev => prev.filter(c => c.clientId !== clientId))
      setDeleteTarget(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete client')
      setDeleteTarget(null)
    }
  }

  const filtered = clients.filter(c =>
    c.clientId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.clientName ?? '').toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="container-standard">
      <PageHeader
        icon={Fingerprint}
        title={t('pages:oidc.title', 'OIDC Clients')}
        description={t('pages:oidc.description', 'Manage OAuth 2.0 / OpenID Connect client registrations')}
        actions={
          <Button variant="primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1" />
            {t('pages:oidc.create', 'Register Client')}
          </Button>
        }
      />

      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t('pages:oidc.search', 'Search by client ID or name...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : error ? (
        <Alert variant="error">{error}</Alert>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">
            {searchQuery
              ? t('pages:oidc.noResults', 'No clients match your search.')
              : t('pages:oidc.empty', 'No OIDC clients registered.')}
          </div>
        </Card>
      ) : (
        <Table variant="modern">
          <TableHeader variant="modern">
            <TableRow variant="modern">
              <TableHead variant="modern">{t('pages:oidc.columns.clientId', 'Client ID')}</TableHead>
              <TableHead variant="modern">{t('pages:oidc.columns.name', 'Name')}</TableHead>
              <TableHead variant="modern">{t('pages:oidc.columns.authMethod', 'Auth Method')}</TableHead>
              <TableHead variant="modern">{t('pages:oidc.columns.grantTypes', 'Grant Types')}</TableHead>
              <TableHead variant="modern">{t('pages:oidc.columns.status', 'Status')}</TableHead>
              <TableHead variant="modern">{t('pages:oidc.columns.lastToken', 'Last Token')}</TableHead>
              <TableHead variant="modern">{t('pages:oidc.columns.actions', 'Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody variant="modern">
            {filtered.map(client => (
              <TableRow key={client.clientId} variant="modern">
                <TableCell>
                  <div className="flex items-center gap-2">
                    <ClientIdCell clientId={client.clientId} />
                    {client.isProtected && (
                      <Badge variant="purple" className="text-[10px] px-1 py-0">
                        {t('pages:oidc.protected', 'Protected')}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-medium">
                  {client.clientName || '-'}
                </TableCell>
                <TableCell>
                  <Badge variant="default">
                    {authMethodLabel(client.tokenEndpointAuthMethod)}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-slate-500">
                  {(client.grantTypes || []).map(grantTypeLabel).join(', ')}
                </TableCell>
                <TableCell>
                  <Badge variant={statusVariant[client.status] ?? 'default'}>
                    {client.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-slate-400">
                  {client.lastTokenIssuedAt ? formatDate(client.lastTokenIssuedAt) : '-'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" aria-label={t('pages:oidc.actions.edit', 'Edit')} onClick={() => setEditClient(client)}>
                      <Pencil size={14} />
                    </Button>
                    {!client.isProtected && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('pages:oidc.actions.delete', 'Delete')}
                        onClick={() => setDeleteTarget(client)}
                      >
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {showCreate && (
        <CreateClientDialog
          onClose={() => setShowCreate(false)}
          onCreated={(c) => {
            setClients(prev => [c, ...prev])
            setShowCreate(false)
          }}
        />
      )}

      {editClient && (
        <EditClientDialog
          client={editClient}
          onClose={() => setEditClient(null)}
          onUpdated={(c) => {
            setClients(prev => prev.map(p => p.clientId === c.clientId ? c : p))
            setEditClient(null)
          }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          client={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => handleDeleteConfirmed(deleteTarget.clientId)}
        />
      )}
    </div>
  )
}

// --- Delete Confirmation Dialog (type client ID to confirm) ---

function DeleteConfirmDialog({
  client,
  onClose,
  onConfirm,
}: {
  client: OIDCClient
  onClose: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation(['pages', 'common'])
  const [confirmText, setConfirmText] = useState('')
  const isMatch = confirmText === client.clientId

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('pages:oidc.dialog.deleteTitle', 'Delete OIDC Client')}</DialogTitle>
          <DialogDescription>
            {t('pages:oidc.dialog.deleteWarning', 'This action cannot be undone. All tokens issued to this client will be invalidated.')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Alert variant="warning">
            {t('pages:oidc.dialog.typeToConfirm', 'Type the client ID to confirm deletion:')}
            <span className="font-mono font-bold ml-1">{client.clientId}</span>
          </Alert>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={client.clientId}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common:cancel', 'Cancel')}</Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={!isMatch}
          >
            {t('pages:oidc.dialog.deleteButton', 'Delete Client')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- Helpers ---

function authMethodLabel(method: TokenEndpointAuthMethod): string {
  return AUTH_METHODS.find(m => m.value === method)?.label ?? method
}

function grantTypeLabel(gt: OIDCGrantType): string {
  return GRANT_TYPES.find(g => g.value === gt)?.label ?? gt
}

function ClientIdCell({ clientId }: { clientId: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    await navigator.clipboard.writeText(clientId)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <div className="flex items-center gap-1.5 font-mono text-xs">
      <span className="truncate max-w-[180px]">{clientId}</span>
      <Button variant="ghost" size="sm" onClick={handleCopy} className="text-slate-400 hover:text-slate-600 h-auto p-0">
        {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
      </Button>
    </div>
  )
}

// --- Create Dialog ---

function CreateClientDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (c: OIDCClient) => void
}) {
  const { t } = useTranslation(['pages', 'common'])
  const [clientId, setClientId] = useState('')
  const [clientName, setClientName] = useState('')
  const [authMethod, setAuthMethod] = useState<TokenEndpointAuthMethod>('none')
  const [grantTypes, setGrantTypes] = useState<OIDCGrantType[]>(['authorization_code'])
  const [scopes, setScopes] = useState<string[]>(['openid', 'profile', 'email'])
  const [redirectUris, setRedirectUris] = useState('')
  const [emailDomains, setEmailDomains] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggleGrantType = (gt: OIDCGrantType) => {
    setGrantTypes(prev =>
      prev.includes(gt) ? prev.filter(g => g !== gt) : [...prev, gt]
    )
  }

  const toggleScope = (scope: string) => {
    setScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    )
  }

  const handleSubmit = async () => {
    if (!clientId.trim()) return
    setIsSubmitting(true)
    setError(null)
    try {
      const req: CreateOIDCClientRequest = {
        clientId: clientId.trim(),
        clientName: clientName.trim(),
        tokenEndpointAuthMethod: authMethod,
        grantTypes,
        allowedScopes: scopes,
        redirectUris: redirectUris.split('\n').map(u => u.trim()).filter(Boolean),
        allowedEmailDomains: emailDomains ? emailDomains.split(',').map(d => d.trim()).filter(Boolean) : undefined,
      }
      const created = await createOIDCClient(req)
      onCreated(created)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create client')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('pages:oidc.dialog.createTitle', 'Register OIDC Client')}</DialogTitle>
          <DialogDescription>
            {t('pages:oidc.dialog.createDescription', 'Configure a new OAuth 2.0 / OIDC client registration.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          {error && <Alert variant="error">{error}</Alert>}

          <div className="space-y-2">
            <Label htmlFor="client-id">{t('pages:oidc.fields.clientId', 'Client ID')}</Label>
            <Input
              id="client-id"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={t('pages:oidc.fields.clientIdPlaceholder')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="client-name">{t('pages:oidc.fields.clientName', 'Display Name')}</Label>
            <Input
              id="client-name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder={t('pages:oidc.fields.clientNamePlaceholder')}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('pages:oidc.fields.authMethod', 'Token Endpoint Auth Method')}</Label>
            <Select value={authMethod} onValueChange={(v) => setAuthMethod(v as TokenEndpointAuthMethod)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {AUTH_METHODS.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('pages:oidc.fields.grantTypes', 'Grant Types')}</Label>
            <div className="flex flex-wrap gap-2">
              {GRANT_TYPES.map(gt => (
                <Button
                  key={gt.value}
                  variant={grantTypes.includes(gt.value) ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => toggleGrantType(gt.value)}
                >
                  {gt.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('pages:oidc.fields.scopes', 'Allowed Scopes')}</Label>
            <div className="flex flex-wrap gap-2">
              {COMMON_SCOPES.map(scope => (
                <Button
                  key={scope}
                  variant={scopes.includes(scope) ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => toggleScope(scope)}
                >
                  {scope}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="redirect-uris">{t('pages:oidc.fields.redirectUris', 'Redirect URIs (one per line)')}</Label>
            <Textarea
              id="redirect-uris"
              value={redirectUris}
              onChange={(e) => setRedirectUris(e.target.value)}
              rows={3}
              placeholder={t('pages:oidc.fields.redirectUrisPlaceholder')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email-domains">{t('pages:oidc.fields.emailDomains', 'Allowed Email Domains (comma-separated)')}</Label>
            <Input
              id="email-domains"
              value={emailDomains}
              onChange={(e) => setEmailDomains(e.target.value)}
              placeholder={t('pages:oidc.fields.emailDomainsPlaceholder')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common:cancel', 'Cancel')}</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!clientId.trim() || isSubmitting}
            isLoading={isSubmitting}
          >
            {t('pages:oidc.create', 'Register Client')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- Edit Dialog ---

function EditClientDialog({
  client,
  onClose,
  onUpdated,
}: {
  client: OIDCClient
  onClose: () => void
  onUpdated: (c: OIDCClient) => void
}) {
  const { t } = useTranslation(['pages', 'common'])
  const [clientName, setClientName] = useState(client.clientName || '')
  const [status, setStatus] = useState<OIDCClientStatus>(client.status)
  const [scopes, setScopes] = useState<string[]>(client.allowedScopes || [])
  const [redirectUris, setRedirectUris] = useState((client.redirectUris || []).join('\n'))
  const [emailDomains, setEmailDomains] = useState((client.allowedEmailDomains || []).join(', '))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggleScope = (scope: string) => {
    setScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    )
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      const req: UpdateOIDCClientRequest = {
        clientName: clientName.trim() || undefined,
        allowedScopes: scopes,
        ...(!client.isProtected && { status, redirectUris: redirectUris.split('\n').map(u => u.trim()).filter(Boolean) }),
        allowedEmailDomains: emailDomains ? emailDomains.split(',').map(d => d.trim()).filter(Boolean) : undefined,
      }
      const updated = await updateOIDCClient(client.clientId, req)
      onUpdated(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update client')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('pages:oidc.dialog.editTitle', 'Edit OIDC Client')}</DialogTitle>
          <DialogDescription>
            <span className="font-mono text-xs">{client.clientId}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          {error && <Alert variant="error">{error}</Alert>}

          <div className="space-y-2">
            <Label htmlFor="edit-name">{t('pages:oidc.fields.clientName', 'Display Name')}</Label>
            <Input
              id="edit-name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('pages:oidc.fields.status', 'Status')}</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as OIDCClientStatus)} disabled={client.isProtected}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t('pages:oidc.status.active', 'Active')}</SelectItem>
                <SelectItem value="suspended">{t('pages:oidc.status.suspended', 'Suspended')}</SelectItem>
                <SelectItem value="revoked">{t('pages:oidc.status.revoked', 'Revoked')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('pages:oidc.fields.scopes', 'Allowed Scopes')}</Label>
            <div className="flex flex-wrap gap-2">
              {COMMON_SCOPES.map(scope => (
                <Button
                  key={scope}
                  variant={scopes.includes(scope) ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => toggleScope(scope)}
                >
                  {scope}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-uris">{t('pages:oidc.fields.redirectUris', 'Redirect URIs (one per line)')}</Label>
            <Textarea
              id="edit-uris"
              value={redirectUris}
              onChange={(e) => setRedirectUris(e.target.value)}
              readOnly={client.isProtected}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-domains">{t('pages:oidc.fields.emailDomains', 'Allowed Email Domains (comma-separated)')}</Label>
            <Input
              id="edit-domains"
              value={emailDomains}
              onChange={(e) => setEmailDomains(e.target.value)}
            />
          </div>

          {/* Read-only metadata */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-700 space-y-2 text-xs text-slate-500">
            <div className="flex justify-between">
              <span>{t('pages:oidc.fields.authMethod', 'Auth Method')}</span>
              <span className="font-mono">{authMethodLabel(client.tokenEndpointAuthMethod)}</span>
            </div>
            <div className="flex justify-between">
              <span>{t('pages:oidc.fields.grantTypes', 'Grant Types')}</span>
              <span>{(client.grantTypes || []).map(grantTypeLabel).join(', ')}</span>
            </div>
            {client.hostId && (
              <div className="flex justify-between">
                <span>{t('pages:oidc.fields.hostId', 'Host ID')}</span>
                <span className="font-mono">{client.hostId}</span>
              </div>
            )}
            {client.spiffeId && (
              <div className="flex justify-between">
                <span>{t('pages:oidc.fields.spiffeId', 'SPIFFE ID')}</span>
                <span className="font-mono truncate max-w-[250px]">{client.spiffeId}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>{t('pages:oidc.fields.created', 'Created')}</span>
              <span>{formatDate(client.createdAt)}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common:cancel', 'Cancel')}</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            isLoading={isSubmitting}
          >
            {t('common:save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
